/**
 * useTimetable Hook
 *
 * Provides operations for timetable sessions, session types, and task type mappings.
 * Part of US-C2-0B2: Timetable Management UI
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

/**
 * Hook to fetch all timetable sessions with their session type details
 * @returns {Object} { sessions, loading, error, refetch }
 */
export function useTimetableSessions() {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('timetable_sessions')
        .select(`
          *,
          session_type:session_types (
            id,
            name,
            description,
            colour
          )
        `)
        .eq('is_active', true)
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true })

      if (fetchError) throw fetchError

      setSessions(data || [])
    } catch (err) {
      console.error('[useTimetableSessions] Error:', err)
      setError(err.message)
      setSessions([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  return {
    sessions,
    loading,
    error,
    refetch: fetchSessions
  }
}

/**
 * Hook to fetch all session types
 * @returns {Object} { sessionTypes, loading, error, refetch }
 */
export function useSessionTypes() {
  const [sessionTypes, setSessionTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSessionTypes = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('session_types')
        .select('*')
        .order('name', { ascending: true })

      if (fetchError) throw fetchError

      setSessionTypes(data || [])
    } catch (err) {
      console.error('[useSessionTypes] Error:', err)
      setError(err.message)
      setSessionTypes([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSessionTypes()
  }, [fetchSessionTypes])

  return {
    sessionTypes,
    loading,
    error,
    refetch: fetchSessionTypes
  }
}

/**
 * Hook to fetch all task type to session type mappings
 * @returns {Object} { mappings, loading, error, refetch }
 */
export function useTaskTypeMappings() {
  const [mappings, setMappings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchMappings = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('task_type_session_mapping')
        .select(`
          *,
          session_type:session_types (
            id,
            name,
            colour
          )
        `)
        .order('event_type', { ascending: true })

      if (fetchError) throw fetchError

      setMappings(data || [])
    } catch (err) {
      console.error('[useTaskTypeMappings] Error:', err)
      setError(err.message)
      setMappings([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMappings()
  }, [fetchMappings])

  return {
    mappings,
    loading,
    error,
    refetch: fetchMappings
  }
}

/**
 * Hook for timetable session mutations
 * @returns {Object} Mutation functions and state
 */
export function useTimetableMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Create a new timetable session
   * @param {Object} sessionData - Session data
   * @returns {Promise<Object>} Created session
   */
  const createSession = useCallback(async (sessionData) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: createError } = await supabase
        .from('timetable_sessions')
        .insert(sessionData)
        .select(`
          *,
          session_type:session_types (
            id,
            name,
            description,
            colour
          )
        `)
        .single()

      if (createError) throw createError

      toast.success('Session created successfully')
      return data
    } catch (err) {
      console.error('[useTimetableMutations.createSession] Error:', err)
      setError(err.message)
      toast.error('Failed to create session: ' + err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update an existing timetable session
   * @param {string} sessionId - Session UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated session
   */
  const updateSession = useCallback(async (sessionId, updates) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: updateError } = await supabase
        .from('timetable_sessions')
        .update(updates)
        .eq('id', sessionId)
        .select(`
          *,
          session_type:session_types (
            id,
            name,
            description,
            colour
          )
        `)
        .single()

      if (updateError) throw updateError

      toast.success('Session updated successfully')
      return data
    } catch (err) {
      console.error('[useTimetableMutations.updateSession] Error:', err)
      setError(err.message)
      toast.error('Failed to update session: ' + err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Delete a timetable session (soft delete by setting is_active = false)
   * @param {string} sessionId - Session UUID
   * @returns {Promise<void>}
   */
  const deleteSession = useCallback(async (sessionId) => {
    setLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await supabase
        .from('timetable_sessions')
        .update({ is_active: false })
        .eq('id', sessionId)

      if (deleteError) throw deleteError

      toast.success('Session deleted successfully')
    } catch (err) {
      console.error('[useTimetableMutations.deleteSession] Error:', err)
      setError(err.message)
      toast.error('Failed to delete session: ' + err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Create a new session type
   * @param {Object} sessionTypeData - Session type data (name, description, colour)
   * @returns {Promise<Object>} Created session type
   */
  const createSessionType = useCallback(async (sessionTypeData) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: createError } = await supabase
        .from('session_types')
        .insert(sessionTypeData)
        .select()
        .single()

      if (createError) throw createError

      toast.success('Session type created successfully')
      return data
    } catch (err) {
      console.error('[useTimetableMutations.createSessionType] Error:', err)
      setError(err.message)
      toast.error('Failed to create session type: ' + err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update an existing session type
   * @param {string} sessionTypeId - Session type UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated session type
   */
  const updateSessionType = useCallback(async (sessionTypeId, updates) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: updateError } = await supabase
        .from('session_types')
        .update(updates)
        .eq('id', sessionTypeId)
        .select()
        .single()

      if (updateError) throw updateError

      toast.success('Session type updated successfully')
      return data
    } catch (err) {
      console.error('[useTimetableMutations.updateSessionType] Error:', err)
      setError(err.message)
      toast.error('Failed to update session type: ' + err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Create a new task type mapping
   * @param {Object} mappingData - Mapping data (event_type, session_type_id)
   * @returns {Promise<Object>} Created mapping
   */
  const createMapping = useCallback(async (mappingData) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: createError } = await supabase
        .from('task_type_session_mapping')
        .insert(mappingData)
        .select(`
          *,
          session_type:session_types (
            id,
            name,
            colour
          )
        `)
        .single()

      if (createError) throw createError

      toast.success('Task mapping created successfully')
      return data
    } catch (err) {
      console.error('[useTimetableMutations.createMapping] Error:', err)
      setError(err.message)
      toast.error('Failed to create mapping: ' + err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Delete a task type mapping
   * @param {string} mappingId - Mapping UUID
   * @returns {Promise<void>}
   */
  const deleteMapping = useCallback(async (mappingId) => {
    setLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await supabase
        .from('task_type_session_mapping')
        .delete()
        .eq('id', mappingId)

      if (deleteError) throw deleteError

      toast.success('Task mapping deleted successfully')
    } catch (err) {
      console.error('[useTimetableMutations.deleteMapping] Error:', err)
      setError(err.message)
      toast.error('Failed to delete mapping: ' + err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    createSession,
    updateSession,
    deleteSession,
    createSessionType,
    updateSessionType,
    createMapping,
    deleteMapping
  }
}

export default useTimetableSessions
